import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { CMYKColor, RGBColor } from '@company-box/models';
import { cmykToHex, cssColorToHex, hexToCmyk, hexToRgb, rgbToHex } from '@company-box/utils';
// Reuses the original's own ColorPickerChange (not a re-declared duplicate, and not re-exported
// here either) - two `export interface ColorPickerChange` in different files would collide once
// both are re-exported from the same public-api.ts barrel.
import { ColorPickerChange } from './color-picker.component';

// Not yet in TS's lib.dom.d.ts - Chrome/Edge only (see eyeDropperSupported).
interface EyeDropperResult {
  sRGBHex: string;
}
interface EyeDropper {
  open(): Promise<EyeDropperResult>;
}
declare const EyeDropper: { new (): EyeDropper } | undefined;

// Cosmetic CMYK color picker (see [[design3d_cmyk_color_picker]]) - a swatch button that opens a
// popover defaulting to CMYK sliders, with RGB and Hex tabs as fallbacks/escape hatches. The hex
// and the CMYK value are always emitted together, so a caller storing the emitted value in the
// design JSON never ends up with one of the two missing - see hexToCmyk()'s use below whenever the
// color came from a channel other than the CMYK sliders (external [hex]/[cmyk] input changes, or
// the RGB/Hex tabs being edited directly).
@Component({
  standalone: true,
  selector: 'app-color-picker-redesign',
  templateUrl: './color-picker-redesign.component.html',
  styleUrls: ['./color-picker-redesign.component.scss'],
  imports: [FormsModule, PopoverModule, MatTooltipModule],
})
export class ColorPickerRedesignComponent implements OnChanges {
  @Input() hex = '#000000';
  @Input() cmyk?: CMYKColor;
  @Input() ariaLabel = 'Choose color';

  @Output() colorChange = new EventEmitter<ColorPickerChange>();

  mode: 'cmyk' | 'rgb' | 'hex' = 'cmyk';
  displayHex = '#000000';
  displayCmyk: CMYKColor = { c: 0, m: 0, y: 0, k: 100 };
  displayRgb: RGBColor = { r: 0, g: 0, b: 0 };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['hex'] || changes['cmyk']) {
      // [hex] isn't always actually hex - a caller that seeds this straight from a Fabric.js
      // object's fill/stroke (an unset one reads back as the CSS default 'rgb(0, 0, 0)') would
      // otherwise flow that string through untouched and show it verbatim in the Hex tab.
      this.displayHex = cssColorToHex(this.hex);
      this.displayCmyk = this.cmyk ?? hexToCmyk(this.displayHex);
      this.displayRgb = hexToRgb(this.displayHex);
    }
  }

  readonly eyeDropperSupported = typeof EyeDropper !== 'undefined';

  // Drives .color-picker-thumb's slide position (translateX(activeIndex * 100%)) - same sliding-
  // indicator pattern as the gradient picker's Solid/Linear/Radial toggle
  // (gradient-picker-redesign.component.ts's own activeIndex).
  get activeIndex(): number {
    return this.mode === 'cmyk' ? 0 : this.mode === 'rgb' ? 1 : 2;
  }

  setMode(mode: 'cmyk' | 'rgb' | 'hex'): void {
    this.mode = mode;
  }

  async openEyeDropper(): Promise<void> {
    if (!this.eyeDropperSupported) {
      return;
    }
    try {
      const result = await new EyeDropper!().open();
      this.onHexChange(result.sRGBHex);
    } catch {
      // User cancelled the pick (Esc or click outside) - nothing to do.
    }
  }

  onCmykChannelChange(channel: keyof CMYKColor, value: number): void {
    this.displayCmyk = { ...this.displayCmyk, [channel]: value };
    this.displayHex = cmykToHex(this.displayCmyk);
    this.displayRgb = hexToRgb(this.displayHex);
    this.emitChange();
  }

  onRgbChannelChange(channel: keyof RGBColor, value: number): void {
    this.displayRgb = { ...this.displayRgb, [channel]: value };
    this.displayHex = rgbToHex(this.displayRgb);
    this.displayCmyk = hexToCmyk(this.displayHex);
    this.emitChange();
  }

  onHexChange(value: string): void {
    // cssColorToHex() also expands a 3-digit shorthand and tolerates a missing leading "#" - both
    // needed here since the plain text field (.color-picker-hex-text) doesn't require the user to
    // type a full "#rrggbb" - hexToCmyk()/hexToRgb() (color-conversion.ts) only handle that exact
    // form (fixed slice offsets) and would otherwise corrupt every channel or parse as NaN.
    const hex = cssColorToHex(value);
    this.displayHex = hex;
    this.displayCmyk = hexToCmyk(hex);
    this.displayRgb = hexToRgb(hex);
    this.emitChange();
  }

  private emitChange(): void {
    this.colorChange.emit({ hex: this.displayHex, cmyk: this.displayCmyk });
  }
}
